import React from 'react';
import ReactPlayer from 'react-player';
import PropTypes from 'prop-types';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import { Card } from '@mui/material';
import { isEmpty, isEqual, find } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import * as materialStyle from '@mui/material/styles';
import Loading from '../Loading';
import Slider from '../Slider';

import * as videoOps from '../../../api/Videos/video-operations';
import * as annotationOps from '../../../api/Annotations/annotation-operations';
import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import Annotations from '../../../api/Annotations/Annotations';

import useTimeStore from '../ZustandProvider';

const YOUTUBE_URL_EXPRESSION = /^(https?:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/;
const FACEBOOK_URL_EXPRESSION = /^(https?:\/\/)?(?:www\.)?facebook\.com\/(?:(?:\w)*#!\/)?(?:pages\/)?(?:[\w-]*\/)*([\w-]*)/;
const TWITCH_URL_EXPRESSION = 'https://(?:clips|www).twitch.tv/(?:(?:[a-z]+)/clip/)?([a-zA-Z]+)';
const HEIGHT_OFFSET_VIDEO = 0.3;

const LightTooltip = materialStyle.styled(({ className, ...props }) => (
  <Tooltip
    classes={{
      popper: className
    }}
    {...props}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)'
  }
}));

const VideoAnnotationsMaps = ({
  loading,
  videoSelected,
  annotations,
  indexAnnotation,
  handleSelectAnnotation,
  handlePropVideo,
  handleVideoTime,
  changeVideoTime,
  isNotesComponent,
  isMobile
}) => {
  if (loading) return <Loading />;
  const drawerWidth = useTimeStore(state => state.drawerWidth);

  const [player, setPlayer] = React.useState({});
  const urlVideo = videoOps.getURL(videoSelected);
  const fileExtension = urlVideo.substring(urlVideo.lastIndexOf('.') + 1, urlVideo.length);

  const startAnnotation =
    !isEmpty(annotations) && indexAnnotation < annotations.length - 1
      ? annotations[indexAnnotation]
      : {};
  const [selectedAnnotation, setSelectedAnnotation] = React.useState(startAnnotation);
  const [propVideo, setPropVideo] = React.useState({ duration: 0, width: 0, height: 0 });
  const [videoTime, setVideoTime] = React.useState(
    Math.round(annotationOps.getVideoTime(selectedAnnotation) * 1e-6)
  );

  const [isPlaying, setIsPlaying] = React.useState(false);

  const playerVars = {
    start: Math.round(annotationOps.getVideoTime(selectedAnnotation) * 1e-6)
  };

  const attributes = {
    autoPlay: false,
    muted: true
  };

  const configSourceVideo = {
    youtube: { playerVars },
    twitch: { playerVars },
    facebook: { playerVars },
    file: { attributes }
  };

  const checkSourceVideo = url => {
    let sourceVideo = '';
    const patronYoutube = new RegExp(YOUTUBE_URL_EXPRESSION);
    const patronFacebook = new RegExp(FACEBOOK_URL_EXPRESSION);
    const patronTwitch = new RegExp(TWITCH_URL_EXPRESSION);
    if (patronYoutube.test(url)) sourceVideo = 'youtube';
    if (patronFacebook.test(url)) sourceVideo = 'facebook';
    if (patronTwitch.test(url)) sourceVideo = 'twitch';
    if (fileExtension === 'MP4' || fileExtension === 'mp4') sourceVideo = 'file';
    return sourceVideo;
  };

  const syncroTimePlayerSlider = (elementClick, eventTime) => {
    if (elementClick === 'player') {
      if (isPlaying) {
        const newAnnotationSelect = find(annotations, el =>
          isEqual(
            Math.round(eventTime.playedSeconds),
            Math.round(annotationOps.getVideoTime(el) * 1e-6)
          )
        );

        if (!isEmpty(newAnnotationSelect)) {
          const index = annotations.indexOf(newAnnotationSelect);
          handleSelectAnnotation(newAnnotationSelect, index);
        }
      }
      setVideoTime(eventTime.playedSeconds * 1e6);
      handleVideoTime(eventTime.playedSeconds);
    } else if (elementClick === 'slider') {
      player.seekTo(eventTime * 1e-6);
    }
  };

  const handleTimeSlider = (property, value) => {
    if (property === 'videoTime') {
      syncroTimePlayerSlider('slider', value);
    } else if (property === 'selectedAnnotation') {
      const index = annotations.indexOf(value);
      handleSelectAnnotation(value, index);
    }
  };

  const onVideoReady = event => {
    let newDuration = 0;
    let widthPlayer = 0;
    let heightPlayer = 0;
    const checkSource = checkSourceVideo(urlVideo);
    const internalPlayer = event.getInternalPlayer();
    if (!isEmpty(event) && !isEmpty(internalPlayer)) {
      newDuration = isEqual(checkSource, 'twitch')
        ? internalPlayer.getVideo()
        : event.getDuration();
      if (isEqual(checkSource, 'youtube')) {
        const sizePlayer = internalPlayer.getSize();
        widthPlayer = sizePlayer.width;
        heightPlayer = sizePlayer.height;
      } else if (isEqual(checkSource, 'file')) {
        const { clientWidth, clientHeight } = internalPlayer;
        widthPlayer = clientWidth;
        heightPlayer = clientHeight;
      }
    }

    setPropVideo({
      duration: isEqual(checkSource, 'twitch') ? newDuration * 1e3 : newDuration * 1e6,
      width: widthPlayer,
      height: heightPlayer
    });
    setPlayer(event);
    handlePropVideo({ duration: newDuration, width: widthPlayer, height: heightPlayer });
  };

  React.useEffect(() => {
    const checkSource = checkSourceVideo(urlVideo);
    if (!isEmpty(player) && checkSource === 'youtube') {
      const internalPlayer = player.getInternalPlayer();
      const sizePlayer = internalPlayer.getSize();
      if (drawerWidth !== 100 / 3) {
        const width =
          drawerWidth !== 100 / 3 ? sizePlayer.width * ((3 * drawerWidth) / 100) : sizePlayer.width;
        const height =
          drawerWidth !== 100 / 3
            ? sizePlayer.height * ((3 * drawerWidth) / 100)
            : sizePlayer.height;
        setPropVideo({ ...propVideo, width, height });
      }
    }
  }, [drawerWidth]);

  React.useEffect(() => {
    let newSelectedAnnotation = {};
    if (
      !isEqual(indexAnnotation, -1) &&
      indexAnnotation < annotations.length - 1 &&
      !isEmpty(annotations)
    ) {
      newSelectedAnnotation = annotations[indexAnnotation];
      setSelectedAnnotation(annotations[indexAnnotation]);
    } else {
      setSelectedAnnotation({});
    }

    const time = annotationOps.getVideoTime(newSelectedAnnotation);
    if (!isEmpty(player) && !isEqual(videoTime, changeVideoTime)) {
      const newTime = isEqual(changeVideoTime, -1) ? time : changeVideoTime;
      player.seekTo(newTime * 1e-6);
    }
  }, [indexAnnotation, changeVideoTime]);

  React.useEffect(() => {
    if (!isEmpty(player) && !isEmpty(selectedAnnotation)) {
      const startTime = annotationOps.getVideoTime(selectedAnnotation);
      syncroTimePlayerSlider('slider', startTime);
    }
  }, [player]);

  const popupName = (
    <div>
      <p>{templateOps.getName(annotationOps.getTemplate(selectedAnnotation))}</p>
      <FontAwesomeIcon color="blue" icon="hand-pointer" style={{ marginLeft: '35%' }} />
    </div>
  );
  if (!isEmpty(urlVideo))
    return (
      <>
        <LightTooltip
          open={
            isEqual(
              Math.round(videoTime * 1e-6),
              Math.round(annotationOps.getVideoTime(selectedAnnotation) * 1e-6)
            ) &&
            !isPlaying &&
            !isEmpty(selectedAnnotation)
          }
          key="Annotation-marker"
          title={popupName}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -200]
                  }
                }
              ]
            }
          }}
        >
          <Card key="card" style={{ marginTop: '5%' }}>
            <ReactPlayer
              className="react-player-maps"
              config={configSourceVideo}
              controls
              loop={false}
              muted
              playbackRate={1}
              url={urlVideo}
              onReady={onVideoReady}
              onProgress={event => syncroTimePlayerSlider('player', event)}
              onPlay={() => setIsPlaying(!isPlaying)}
              height={`${window.innerHeight * HEIGHT_OFFSET_VIDEO}px`}
              width="100%"
            />
          </Card>
        </LightTooltip>
        <br />
        <Slider
          annotations={annotations}
          domain={propVideo.duration}
          videoTime={videoTime}
          changeVideoContainerStateProperty={handleTimeSlider}
          isVideoSynchronizer
          isVideoVisualizer
          isNotesComponent={isNotesComponent}
          isMobile={isMobile}
        />
      </>
    );
  return '';
};

VideoAnnotationsMaps.defaultProps = {
  annotations: [],
  indexAnnotation: -1,
  handleSelectAnnotation: () => {},
  handlePropVideo: () => {},
  handleVideoTime: () => {},
  changeVideoTime: -1,
  isNotesComponent: true,
  isMobile: false
};

VideoAnnotationsMaps.propTypes = {
  loading: PropTypes.bool.isRequired,
  videoSelected: PropTypes.object.isRequired,
  annotations: PropTypes.array,
  indexAnnotation: PropTypes.number,
  handleSelectAnnotation: PropTypes.func,
  handlePropVideo: PropTypes.func,
  handleVideoTime: PropTypes.func,
  changeVideoTime: PropTypes.number,
  isNotesComponent: PropTypes.bool,
  isMobile: PropTypes.bool
};
export default withTracker(({ order = 1, videoSelected, sortField = 'videoTime' }) => {
  const videoId = videoOps.getId(videoSelected);
  const annotationSub = Meteor.subscribe('annotations.video', videoId);
  const isReady = annotationSub.ready();
  let annotations = null;

  if (isReady) {
    const filters = { 'geoJSON.properties.video': videoId };
    annotations =
      Annotations.find(filters, {
        sort: { [sortField]: order }
      }).fetch() || null;
  }

  return {
    loading: !isReady,
    annotations
  };
})(VideoAnnotationsMaps);
